/**
 * BeamAnalysisPage — calcula Mu/Vu desde geometría + apoyo + cargas.
 *
 * Layout:
 *   left rail  → inputs (geometría, apoyo, cargas, sismo opcional)
 *   middle     → desglose de cargas + fuerzas internas + diagrama
 *   right rail → botón "Diseñar →" y resumen
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Loader2,
  Plus,
  Save,
  Trash2,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useBeamAnalysisStore } from '@/features/analysis/beams/store';
import { useBeamAnalysisQuery } from '@/features/analysis/beams/useBeamAnalysis';
import {
  SLAB_LABELS,
  SOIL_LABELS,
  SUPPORT_HINT,
  SUPPORT_LABELS,
  SYSTEM_LABELS,
  USAGE_LABELS,
  USE_LABELS,
  WALL_LABELS,
  ZONE_LABELS,
  type SlabType,
  type LiveLoadUse,
  type SoilProfile,
  type SupportType,
  type SystemKind,
  type UsageCategory,
  type WallType,
  type ZoneCode,
  type BeamAnalysisRequest,
} from '@/features/analysis/beams/types';
import { useDesign } from '@/features/designs/useDesigns';

export function BeamAnalysisPage() {
  const request = useBeamAnalysisStore((s) => s.request);
  const setGeometry = useBeamAnalysisStore((s) => s.setGeometry);
  const setSupportType = useBeamAnalysisStore((s) => s.setSupportType);
  const setAutoLoads = useBeamAnalysisStore((s) => s.setAutoLoads);
  const setWallOnBeam = useBeamAnalysisStore((s) => s.setWallOnBeam);
  const setCombo = useBeamAnalysisStore((s) => s.setCombo);
  const setMaterials = useBeamAnalysisStore((s) => s.setMaterials);
  const addDistributed = useBeamAnalysisStore((s) => s.addDistributed);
  const updateDistributed = useBeamAnalysisStore((s) => s.updateDistributed);
  const removeDistributed = useBeamAnalysisStore((s) => s.removeDistributed);
  const addPointLoad = useBeamAnalysisStore((s) => s.addPointLoad);
  const updatePointLoad = useBeamAnalysisStore((s) => s.updatePointLoad);
  const removePointLoad = useBeamAnalysisStore((s) => s.removePointLoad);
  const toggleSeismic = useBeamAnalysisStore((s) => s.toggleSeismic);
  const setSeismic = useBeamAnalysisStore((s) => s.setSeismic);
  const loadRequest = useBeamAnalysisStore((s) => s.loadRequest);

  // URL hydration: /app/analysis/beams/:id
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as BeamAnalysisRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const { data, isFetching, isError } = useBeamAnalysisQuery(request);
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);

  const onDesign = () => {
    if (!data) return;
    navigate('/app/beams/new', {
      state: {
        prefill: {
          source: 'analysis',
          analysisId: designId,
          section: {
            type: 'rectangular',
            b: request.geometry.b,
            h: request.geometry.h,
            L_span: request.geometry.L_span_m * 100,
          },
          materials: {
            fc: request.fc,
            fy: request.fy,
            cover: request.cover_cm,
            TMA: request.TMA_cm,
          },
          loads: {
            Mu_neg_left: data.design_prefill.Mu_neg_left,
            Mu_pos_mid: data.design_prefill.Mu_pos_mid,
            Mu_neg_right: data.design_prefill.Mu_neg_right,
            Vu_left: data.design_prefill.Vu_left,
            Vu_right: data.design_prefill.Vu_right,
          },
        },
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header
        className="flex h-14 shrink-0 items-center justify-between border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Análisis E.020 + ACI 318-19 6.5.2
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Análisis de vigas — cálculo de Mu y Vu
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSaveOpen(true)}>
            <Save size={14} strokeWidth={1.8} /> {designId ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={onDesign}
            disabled={!data || isFetching}
            title="Llevar Mu, Vu al módulo de Diseño"
          >
            Diseñar viga <ArrowRight size={14} strokeWidth={2} />
          </Button>
        </div>
      </header>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="analysis_beams"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      {/* Body */}
      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)_300px]">
        {/* ===== LEFT RAIL — INPUTS ===== */}
        <div
          className="overflow-y-auto border-r p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <Section title="Geometría">
            <Row>
              <NumberField
                label="b (cm)"
                value={request.geometry.b}
                onChange={(v) => setGeometry({ b: v })}
              />
              <NumberField
                label="h (cm)"
                value={request.geometry.h}
                onChange={(v) => setGeometry({ h: v })}
              />
            </Row>
            <NumberField
              label="L luz libre (m)"
              value={request.geometry.L_span_m}
              onChange={(v) => setGeometry({ L_span_m: v })}
              step={0.1}
            />
          </Section>

          <Section title="Tipo de apoyo">
            <select
              value={request.support_type}
              onChange={(e) => setSupportType(e.currentTarget.value as SupportType)}
              className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
            >
              {(Object.keys(SUPPORT_LABELS) as SupportType[]).map((k) => (
                <option key={k} value={k}>
                  {SUPPORT_LABELS[k]}
                </option>
              ))}
            </select>
            <div className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {SUPPORT_HINT[request.support_type]}
            </div>
          </Section>

          <Section title="Cargas automáticas">
            <SelectField
              label="Uso (E.020)"
              value={request.auto_loads.use}
              options={USE_LABELS as Record<string, string>}
              onChange={(v) => setAutoLoads({ use: v as LiveLoadUse })}
            />
            <SelectField
              label="Sistema de losa"
              value={request.auto_loads.slab_type}
              options={SLAB_LABELS as Record<string, string>}
              onChange={(v) => setAutoLoads({ slab_type: v as SlabType })}
            />
            <NumberField
              label="Ancho tributario (m)"
              value={request.auto_loads.tributary_width_m}
              onChange={(v) => setAutoLoads({ tributary_width_m: v })}
              step={0.1}
            />
            <Row>
              <NumberField
                label="Acabados (kgf/m²)"
                value={request.auto_loads.acabados_kgf_m2}
                onChange={(v) => setAutoLoads({ acabados_kgf_m2: v })}
              />
              <NumberField
                label="Tabiquería"
                value={request.auto_loads.tabiqueria_kgf_m2}
                onChange={(v) => setAutoLoads({ tabiqueria_kgf_m2: v })}
              />
            </Row>
            <NumberField
              label="Cielo raso (kgf/m²)"
              value={request.auto_loads.cielo_raso_kgf_m2}
              onChange={(v) => setAutoLoads({ cielo_raso_kgf_m2: v })}
            />
            <WallOnBeamField
              value={request.auto_loads.wall_on_beam ?? null}
              onChange={setWallOnBeam}
            />
          </Section>

          <Section title="Cargas distribuidas adicionales">
            {request.extra_distributed.map((d, i) => (
              <div
                key={i}
                className="mb-2 rounded-[var(--radius-sm)] border p-2"
                style={{ borderColor: 'var(--border-default)', background: 'var(--surface-2)' }}
              >
                <Row>
                  <select
                    value={d.kind}
                    onChange={(e) => updateDistributed(i, { kind: e.currentTarget.value as 'dead' | 'live' })}
                    className="h-8 w-full rounded border bg-[var(--surface-base)] px-2 text-[12px]"
                    style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                  >
                    <option value="dead">Muerta</option>
                    <option value="live">Viva</option>
                  </select>
                  <NumberField
                    label="kgf/m"
                    value={d.magnitude}
                    onChange={(v) => updateDistributed(i, { magnitude: v })}
                    compact
                  />
                </Row>
                <Row>
                  <NumberField
                    label="x_ini (cm)"
                    value={d.x_start_cm}
                    onChange={(v) => updateDistributed(i, { x_start_cm: v })}
                    compact
                  />
                  <NumberField
                    label="x_fin (cm, 0=toda)"
                    value={d.x_end_cm}
                    onChange={(v) => updateDistributed(i, { x_end_cm: v })}
                    compact
                  />
                </Row>
                <button
                  type="button"
                  onClick={() => removeDistributed(i)}
                  className="mt-1 flex w-full items-center justify-center gap-1 rounded text-[11px]"
                  style={{ color: 'var(--danger)' }}
                >
                  <Trash2 size={11} /> Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addDistributed}
              className="flex w-full items-center justify-center gap-1 rounded-[var(--radius-sm)] border border-dashed py-2 text-[12px]"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
            >
              <Plus size={12} /> Agregar carga distribuida
            </button>
          </Section>

          <Section title="Cargas puntuales">
            {request.point_loads.map((p, i) => (
              <div
                key={i}
                className="mb-2 rounded-[var(--radius-sm)] border p-2"
                style={{ borderColor: 'var(--border-default)', background: 'var(--surface-2)' }}
              >
                <Row>
                  <select
                    value={p.kind}
                    onChange={(e) => updatePointLoad(i, { kind: e.currentTarget.value as 'dead' | 'live' })}
                    className="h-8 w-full rounded border bg-[var(--surface-base)] px-2 text-[12px]"
                    style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                  >
                    <option value="dead">Muerta</option>
                    <option value="live">Viva</option>
                  </select>
                  <NumberField
                    label="P (kgf)"
                    value={p.magnitude_kgf}
                    onChange={(v) => updatePointLoad(i, { magnitude_kgf: v })}
                    compact
                  />
                </Row>
                <NumberField
                  label="x desde apoyo izq (cm)"
                  value={p.x_cm}
                  onChange={(v) => updatePointLoad(i, { x_cm: v })}
                  compact
                />
                <input
                  type="text"
                  value={p.label ?? ''}
                  onChange={(e) => updatePointLoad(i, { label: e.currentTarget.value })}
                  placeholder="Descripción (ej. V-204)"
                  className="mt-1 h-7 w-full rounded border bg-[var(--surface-base)] px-2 text-[11px]"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
                <button
                  type="button"
                  onClick={() => removePointLoad(i)}
                  className="mt-1 flex w-full items-center justify-center gap-1 rounded text-[11px]"
                  style={{ color: 'var(--danger)' }}
                >
                  <Trash2 size={11} /> Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPointLoad}
              className="flex w-full items-center justify-center gap-1 rounded-[var(--radius-sm)] border border-dashed py-2 text-[12px]"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
            >
              <Plus size={12} /> Agregar carga puntual
            </button>
          </Section>

          <Section title="Sismo (opcional)">
            <label className="mb-2 flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={request.seismic !== null}
                onChange={(e) => toggleSeismic(e.currentTarget.checked)}
                className="h-3.5 w-3.5 accent-[var(--accent)]"
              />
              Considerar carga sísmica E.030
            </label>
            {request.seismic && (
              <>
                <SelectField
                  label="Zona"
                  value={request.seismic.zone}
                  options={ZONE_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ zone: v as ZoneCode })}
                />
                <SelectField
                  label="Categoría"
                  value={request.seismic.usage}
                  options={USAGE_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ usage: v as UsageCategory })}
                />
                <SelectField
                  label="Suelo"
                  value={request.seismic.soil}
                  options={SOIL_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ soil: v as SoilProfile })}
                />
                <SelectField
                  label="Sistema"
                  value={request.seismic.system}
                  options={SYSTEM_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ system: v as SystemKind })}
                />
                <Row>
                  <NumberField
                    label="hn (m)"
                    value={request.seismic.building_height_m}
                    onChange={(v) => setSeismic({ building_height_m: v })}
                    step={0.5}
                  />
                  <NumberField
                    label="P_sis (kgf)"
                    value={request.seismic.seismic_weight_kgf}
                    onChange={(v) => setSeismic({ seismic_weight_kgf: v })}
                    step={1000}
                  />
                </Row>
                <Row>
                  <NumberField
                    label="Frac. piso"
                    value={request.seismic.floor_shear_fraction}
                    onChange={(v) => setSeismic({ floor_shear_fraction: v })}
                    step={0.05}
                  />
                  <NumberField
                    label="Frac. viga"
                    value={request.seismic.beam_shear_fraction}
                    onChange={(v) => setSeismic({ beam_shear_fraction: v })}
                    step={0.05}
                  />
                </Row>
              </>
            )}
          </Section>

          <Section title="Materiales">
            <Row>
              <NumberField label="f'c" value={request.fc} onChange={(v) => setMaterials({ fc: v })} />
              <NumberField label="fy" value={request.fy} onChange={(v) => setMaterials({ fy: v })} />
            </Row>
            <Row>
              <NumberField
                label="Recub. (cm)"
                value={request.cover_cm}
                onChange={(v) => setMaterials({ cover_cm: v })}
                step={0.5}
              />
              <NumberField
                label="TMA (cm)"
                value={request.TMA_cm}
                onChange={(v) => setMaterials({ TMA_cm: v })}
                step={0.1}
              />
            </Row>
          </Section>

          <Section title="Combinación">
            <div className="flex gap-2">
              {(['E060', 'ACI318'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCombo(c)}
                  className="flex-1 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[12px]"
                  style={{
                    background: request.combo === c ? 'var(--accent)' : 'var(--surface-2)',
                    color: request.combo === c ? 'var(--surface-base)' : 'var(--text-secondary)',
                    borderColor: request.combo === c ? 'var(--accent)' : 'var(--border-default)',
                  }}
                >
                  {c === 'E060' ? 'E.060 (1.4D+1.7L)' : 'ACI (1.2D+1.6L)'}
                </button>
              ))}
            </div>
          </Section>
        </div>

        {/* ===== MIDDLE — RESULTADOS ===== */}
        <div className="overflow-y-auto p-6 scrollbar-thin">
          {isError && (
            <div
              className="mb-4 rounded-[var(--radius-md)] border p-3 text-[13px]"
              style={{ background: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.4)', color: '#fca5a5' }}
            >
              No se pudo calcular el análisis. Revisa los inputs.
            </div>
          )}

          {data && (
            <>
              <BreakdownCard b={data.breakdown} combo={request.combo} />
              <ForcesCard data={data} L={request.geometry.L_span_m} />
              <DiagramCard data={data} L={request.geometry.L_span_m} />
              {data.warnings.length > 0 && <WarningsCard items={data.warnings} />}
              {data.seismic && <SeismicCard s={data.seismic} />}
            </>
          )}

          {!data && isFetching && (
            <div className="flex items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={20} className="mr-2 animate-spin" />
              Calculando…
            </div>
          )}
        </div>

        {/* ===== RIGHT RAIL — RESUMEN ===== */}
        <div
          className="overflow-y-auto border-l p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          {data && (
            <SummaryCard
              data={data}
              onDesign={onDesign}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents — primitives
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div
        className="mb-2 text-[10px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--text-muted)' }}
      >
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
  compact = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  compact?: boolean;
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
      <span>{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.currentTarget.value) || 0)}
        step={step}
        className={`${compact ? 'h-8' : 'h-9'} rounded border bg-[var(--surface-2)] px-2 text-[12.5px] tabular`}
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        className="h-9 rounded border bg-[var(--surface-2)] px-2 text-[12.5px]"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {Object.entries(options).map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

function WallOnBeamField({
  value,
  onChange,
}: {
  value: { wall_type: WallType; height_m: number } | null;
  onChange: (w: { wall_type: WallType; height_m: number } | null) => void;
}) {
  const enabled = value !== null;
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <label className="flex items-center gap-2 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange(e.currentTarget.checked ? { wall_type: 'kk_15', height_m: 2.7 } : null)
          }
          className="h-3.5 w-3.5 accent-[var(--accent)]"
        />
        Tabique sobre la viga
      </label>
      {enabled && value && (
        <div className="mt-2 space-y-1">
          <SelectField
            label="Tipo"
            value={value.wall_type}
            options={WALL_LABELS as Record<string, string>}
            onChange={(v) => onChange({ ...value, wall_type: v as WallType })}
          />
          <NumberField
            label="Altura (m)"
            value={value.height_m}
            onChange={(h) => onChange({ ...value, height_m: h })}
            step={0.1}
            compact
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result cards
// ---------------------------------------------------------------------------

function BreakdownCard({
  b,
  combo,
}: {
  b: import('@/features/analysis/beams/types').LoadBreakdown;
  combo: 'E060' | 'ACI318';
}) {
  const factorD = combo === 'E060' ? 1.4 : 1.2;
  const factorL = combo === 'E060' ? 1.7 : 1.6;
  return (
    <Card title="Cargas calculadas" icon={<Activity size={14} strokeWidth={1.8} />}>
      <div className="grid grid-cols-2 gap-4 text-[12.5px]">
        <div>
          <Heading>Superficiales (kgf/m²)</Heading>
          <RowKV k="Peso losa" v={b.q_slab_self} />
          <RowKV k="Acabados" v={b.q_finishes} />
          <RowKV k="Tabiquería" v={b.q_partitions} />
          <RowKV k="Cielo raso" v={b.q_ceiling} />
          <Divider />
          <RowKV k="q_D total" v={b.q_D_surface} bold />
          <RowKV k="q_L (E.020)" v={b.q_L_surface} bold />
        </div>
        <div>
          <Heading>Lineales (kgf/m)</Heading>
          <RowKV k="w_D automática" v={b.w_D_auto} />
          {b.w_D_extra > 0 && <RowKV k="w_D extra" v={b.w_D_extra} />}
          <RowKV k="w_L automática" v={b.w_L_auto} />
          {b.w_L_extra > 0 && <RowKV k="w_L extra" v={b.w_L_extra} />}
          <Divider />
          <RowKV k={`w_u = ${factorD}D + ${factorL}L`} v={b.w_u_linear} bold accent />
        </div>
      </div>
      {b.beam_self_weight > 0 && (
        <div className="mt-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          Peso propio viga: {b.beam_self_weight.toFixed(0)} kgf/m
          {b.wall_on_beam_load > 0 && (
            <> · Tabique sobre viga: {b.wall_on_beam_load.toFixed(0)} kgf/m</>
          )}
        </div>
      )}
    </Card>
  );
}

function ForcesCard({
  data,
  L,
}: {
  data: import('@/features/analysis/beams/types').BeamAnalysisResponse;
  L: number;
}) {
  const f = data.forces;
  // El backend devuelve Mu en kgf·cm (para el módulo de Diseño existente).
  // En la UI mostramos kgf·m porque es más legible (números 100× más cortos).
  return (
    <Card title="Fuerzas internas (envolvente)" icon={<Zap size={14} strokeWidth={1.8} />}>
      <div className="mb-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
        Apoyo: {data.coefficients_used.support_type as string} · Combo gobernante: {data.governing_combo}
      </div>
      <div className="grid grid-cols-3 gap-3 text-[12px]">
        <ForceItem label="Mu (-) izq" value={f.Mu_neg_left / 100} unit="kgf·m" />
        <ForceItem label="Mu (+) centro" value={f.Mu_pos_mid / 100} unit="kgf·m" />
        <ForceItem label="Mu (-) der" value={f.Mu_neg_right / 100} unit="kgf·m" />
        <ForceItem label="Vu izq" value={f.Vu_left} unit="kgf" />
        <div />
        <ForceItem label="Vu der" value={f.Vu_right} unit="kgf" />
      </div>
      <div className="mt-2 text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
        Luz L = {L.toFixed(2)} m
      </div>
    </Card>
  );
}

function ForceItem({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div
      className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
    >
      <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div className="mt-0.5 tabular text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
        {value.toLocaleString('es-PE', { maximumFractionDigits: 0 })}
      </div>
      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
        {unit}
      </div>
    </div>
  );
}

function DiagramCard({
  data,
  L,
}: {
  data: import('@/features/analysis/beams/types').BeamAnalysisResponse;
  L: number;
}) {
  const W = 600;
  const H = 200;
  const PAD = 30;
  const xs = data.diagram.map((d) => d.x_m);
  const ms = data.diagram.map((d) => d.Mu_kgf_m);
  const vs = data.diagram.map((d) => d.V_kgf);
  const m_max = Math.max(...ms.map(Math.abs), 1);
  const v_max = Math.max(...vs.map(Math.abs), 1);

  const xToPx = (x: number) => PAD + ((W - 2 * PAD) * x) / L;
  // For M: invert Y (positive moments down in engineering convention)
  const mToPx = (m: number) => H / 2 + (H / 2 - PAD) * (m / m_max);
  const vToPx = (v: number) => H / 2 - (H / 2 - PAD) * (v / v_max);

  const mPath = data.diagram.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xToPx(d.x_m)} ${mToPx(d.Mu_kgf_m)}`).join(' ');
  const vPath = data.diagram.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xToPx(d.x_m)} ${vToPx(d.V_kgf)}`).join(' ');

  return (
    <Card title="Diagramas Mu(x) y V(x)" icon={<Activity size={14} strokeWidth={1.8} />}>
      <svg viewBox={`0 0 ${W} ${H * 2 + 20}`} className="w-full">
        {/* Diagrama de momentos */}
        <g>
          <text x={PAD} y={15} fontSize="11" fill="var(--text-muted)">Mu(x)</text>
          <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="var(--border-default)" />
          <path d={mPath} fill="none" stroke="var(--accent)" strokeWidth={1.5} />
          {/* relleno semitransparente bajo la curva */}
          <path
            d={`${mPath} L ${xToPx(L)} ${H / 2} L ${PAD} ${H / 2} Z`}
            fill="var(--accent)"
            opacity={0.12}
          />
        </g>
        {/* Diagrama de cortantes */}
        <g transform={`translate(0, ${H + 20})`}>
          <text x={PAD} y={15} fontSize="11" fill="var(--text-muted)">V(x)</text>
          <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="var(--border-default)" />
          <path d={vPath} fill="none" stroke="#86efac" strokeWidth={1.5} />
          <path
            d={`${vPath} L ${xToPx(L)} ${H / 2} L ${PAD} ${H / 2} Z`}
            fill="#86efac"
            opacity={0.12}
          />
        </g>
      </svg>
      <div className="mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
        Mu en kgf·m · V en kgf · escala automática por máximo absoluto
      </div>
    </Card>
  );
}

function SeismicCard({ s }: { s: import('@/features/analysis/beams/types').SeismicSummary }) {
  return (
    <Card title="Resumen sísmico (E.030 método estático)" icon={<Zap size={14} strokeWidth={1.8} />}>
      <div className="grid grid-cols-3 gap-2 text-[11.5px]">
        <RowKV k="Z" v={s.Z} />
        <RowKV k="U" v={s.U} />
        <RowKV k="S" v={s.S} />
        <RowKV k="R" v={s.R} />
        <RowKV k="C" v={s.C} />
        <RowKV k="T edif" v={s.T_building} unit="s" />
        <RowKV k="Tp" v={s.Tp} unit="s" />
        <RowKV k="Tl" v={s.Tl} unit="s" />
        <RowKV k="V_basal" v={s.V_basal} unit="kgf" />
      </div>
      <Divider />
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <RowKV k="V en viga" v={s.V_element} unit="kgf" bold />
        <RowKV k="M en extremo" v={s.M_element} unit="kgf·m" bold />
      </div>
    </Card>
  );
}

function WarningsCard({ items }: { items: string[] }) {
  return (
    <Card title="Advertencias" icon={<AlertTriangle size={14} strokeWidth={1.8} />}>
      <ul className="list-disc space-y-1 pl-5 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
        {items.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </Card>
  );
}

function SummaryCard({
  data,
  onDesign,
}: {
  data: import('@/features/analysis/beams/types').BeamAnalysisResponse;
  onDesign: () => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          Para diseño
        </div>
        <h3 className="mt-1 text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Resumen
        </h3>
      </div>
      <div
        className="rounded-[var(--radius-md)] border p-3 text-[12px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
      >
        <RowKV k="w_u" v={data.breakdown.w_u_linear} unit="kgf/m" />
        <Divider />
        <RowKV k="Mu(-) izq" v={data.forces.Mu_neg_left / 100} unit="kgf·m" />
        <RowKV k="Mu(+) cnt" v={data.forces.Mu_pos_mid / 100} unit="kgf·m" />
        <RowKV k="Mu(-) der" v={data.forces.Mu_neg_right / 100} unit="kgf·m" />
        <RowKV k="Vu izq" v={data.forces.Vu_left} unit="kgf" />
        <RowKV k="Vu der" v={data.forces.Vu_right} unit="kgf" />
        <Divider />
        <div className="text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
          Combo: {data.governing_combo}
        </div>
      </div>
      <Button variant="primary" size="md" onClick={onDesign}>
        Diseñar viga <ArrowRight size={14} strokeWidth={2} />
      </Button>
      <div className="text-[11px]" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Llevará los Mu y Vu calculados al módulo de Diseño con un banner para
        que sepas que vinieron del análisis. Podrás ajustarlos antes de
        calcular el refuerzo.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny atoms used inside cards
// ---------------------------------------------------------------------------

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="mb-4 rounded-[var(--radius-md)] border p-4"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div
        className="mb-3 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--text-muted)' }}
      >
        <span style={{ color: 'var(--accent)' }}>{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
      {children}
    </div>
  );
}

function RowKV({
  k,
  v,
  unit,
  bold,
  accent,
}: {
  k: string;
  v: number;
  unit?: string;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span style={{ color: 'var(--text-muted)' }}>{k}</span>
      <span
        className={`tabular ${bold ? 'font-semibold' : ''}`}
        style={{ color: accent ? 'var(--accent)' : 'var(--text-primary)' }}
      >
        {v.toLocaleString('es-PE', { maximumFractionDigits: 1 })}
        {unit && <span className="ml-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>{unit}</span>}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1.5 border-t" style={{ borderColor: 'var(--border-subtle)' }} />;
}
